import { useState } from 'react';
import { Cog, RefreshCw, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { format, isToday, parseISO } from 'date-fns';
import { StatusBadge } from '../common/StatusBadge';
import type { ActivityEvent } from '../../types/activity';

interface EventCardProps {
  event: ActivityEvent;
}

const ICON_BG_COLORS: Record<ActivityEvent['type'], string> = {
  job_run: 'bg-accent-purple/20',
  sync: 'bg-info/20',
  slack: 'bg-success/20',
};

const ICON_TEXT_COLORS: Record<ActivityEvent['type'], string> = {
  job_run: 'text-accent-purple-light',
  sync: 'text-info',
  slack: 'text-success',
};

const TYPE_ICONS: Record<ActivityEvent['type'], React.ReactNode> = {
  job_run: <Cog className="h-4 w-4" />,
  sync: <RefreshCw className="h-4 w-4" />,
  slack: <MessageSquare className="h-4 w-4" />,
};

function formatTimestamp(isoString: string): string {
  const date = parseISO(isoString);
  if (isToday(date)) {
    return format(date, 'h:mm a');
  }
  return format(date, 'MMM d, h:mm a');
}

function hasExpandableContent(event: ActivityEvent): boolean {
  if (!event.metadata) return false;
  return !!(
    (event.metadata as Record<string, unknown>).result_summary ||
    (event.metadata as Record<string, unknown>).error
  );
}

function getMetaLine(metadata: Record<string, unknown>): string | null {
  const parts: string[] = [];
  if (metadata.duration) parts.push(`${metadata.duration}s`);
  if (metadata.tokens) parts.push(`${metadata.tokens} tokens`);
  if (metadata.cost) parts.push(`$${Number(metadata.cost).toFixed(4)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function EventCard({ event }: EventCardProps) {
  const [expanded, setExpanded] = useState(false);
  const expandable = hasExpandableContent(event);
  const metaLine =
    event.type === 'job_run' && event.metadata
      ? getMetaLine(event.metadata as Record<string, unknown>)
      : null;

  const expandedContent = expandable
    ? ((event.metadata as Record<string, unknown>).result_summary as string) ||
      ((event.metadata as Record<string, unknown>).error as string) ||
      null
    : null;

  const isError = event.status === 'error';

  return (
    <div
      className={`p-3 border-b border-border-subtle last:border-0 hover:bg-bg-tertiary/50 transition-colors ${
        isError ? 'bg-error/5' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ICON_BG_COLORS[event.type]} ${ICON_TEXT_COLORS[event.type]}`}
        >
          {TYPE_ICONS[event.type]}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">{event.agent}</p>
          <p className="text-sm text-text-secondary">{event.summary}</p>
          {metaLine ? (
            <p className="text-xs text-text-muted mt-0.5">{metaLine}</p>
          ) : null}
        </div>

        {/* Right side */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
          <span className="text-xs text-text-muted whitespace-nowrap">
            {formatTimestamp(event.timestamp)}
          </span>
          <StatusBadge status={event.status} size="sm" />
        </div>

        {/* Expand chevron */}
        {expandable ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 text-text-muted hover:text-text-secondary transition-colors p-0.5 mt-0.5"
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        ) : null}
      </div>

      {/* Expanded detail */}
      {expanded && expandedContent ? (
        <div className="mt-2 ml-11 bg-bg-tertiary rounded p-3">
          <pre className="text-xs text-text-secondary whitespace-pre-wrap break-words font-mono">
            {expandedContent}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

export default EventCard;
